import {
  PUBLIC_VIEW_KEY_LENGTH,
  KEY_LENGTH,
  MAX_PAYLOAD_SIZE,
  NACL_TAG_LENGTH,
} from './../../constants/encryption';
import {
  DISCRIMINATOR_LENGTH,
  DISCRIMINATOR_MESSAGE,
  FEE_LENGTH,
  FILTER_TAG_TOTAL_LENGTH,
  HINT_LENGTH,
  MINIMUM_FEE_PDA_SEED,
  TOTAL_LENGTH as ENVELOPE_TOTAL_LENGTH,
} from './../../constants/envelope';
import {
  COMPUTE_UNIT_LIMIT,
  FAUCET_ID,
  FOUNDATION_ID,
  MICRO_LAMPORTS_PER_LAMPORT,
  SYSTEM_PROGRAM_ID,
  UNLINK_PROGRAM_ID as PROGRAM_ID,
} from './../../constants/general';
import { createDataFrame } from './createDataFrame';
import { createEphemeralEncryptionContext } from './../../utils/crypto/createEphemeralEncryptionContext';
import { encryptLayer } from './../../utils/crypto/encryptLayer';

import { SenderHmacProofInput } from "./../../types/crypto/senderHmacProofInput";
import { Keypair, PublicKey, TransactionInstruction } from '@solana/web3.js';

/**
 * Encrypts a message and builds a Solana TransactionInstruction to be sent
 * to the Unlink program. This includes a hybrid encryption scheme combining
 * ephemeral ECDH and optionally Kyber (post-quantum KEM) for layered confidentiality.
 *
 * @remarks
 * This function must be treated as security-critical. It handles ephemeral key generation,
 * layered encryption, HMAC proof-of-origin, and payload formatting for on-chain posting.
 * All outputs must be forward-secrecy preserving, quantum-tolerant (if Kyber is used),
 * and authenticated via sender-side proof.
 *
 * @returns A TransactionInstruction ready for inclusion in a Solana transaction.
 */
export async function createSendInstruction({
  payer,
  senderViewPublicKey,
  senderViewPrivateKey,
  senderNextViewPublicKey,
  receiverViewPublicKey,
  messagePayload,
  spamMitigationFeeLamports,
  conversationId,
  kyberSecret,
  randomFill,
  createSenderHmacProof,
  filterTag,
}: {
  payer: Keypair;
  senderViewPublicKey: Uint8Array;
  senderViewPrivateKey: Uint8Array;
  senderNextViewPublicKey: Uint8Array;
  receiverViewPublicKey: Uint8Array;
  messagePayload: Uint8Array;
  spamMitigationFeeLamports: bigint;
  conversationId: Uint8Array;
  kyberSecret?: Uint8Array;
  randomFill: (target: Uint8Array) => void;
  createSenderHmacProof: (input: SenderHmacProofInput) => Promise<Uint8Array>;
  filterTag: Uint8Array;
}): Promise<TransactionInstruction> {

  if (filterTag.length !== 6) {
    throw new Error('filterTag must be exactly 6 bytes');
  }

  // Ensure a valid next view key (forward secrecy step); fallback to zero-filled if unused
  if (!senderNextViewPublicKey || senderNextViewPublicKey.length !== PUBLIC_VIEW_KEY_LENGTH) {
    senderNextViewPublicKey = new Uint8Array(PUBLIC_VIEW_KEY_LENGTH);
  }

  // Generate a cryptographic proof (HMAC) that ties this payload to the sender and conversation
  const senderProof = await createSenderHmacProof({
    message: messagePayload,
    conversationId,
    receiverViewPublicKey,
    senderViewPrivateKey,
    senderNextViewPublicKey
  });

  // Ephemeral ECDH setup: generate temporary keypair and compute shared secret + hint
  const {
    ephemeralPrivateKey,
    ephemeralPublicKey,
    sharedSecret,
    sharedSecretHint,
    nonce,
  } = createEphemeralEncryptionContext(receiverViewPublicKey);

  // Pad + format message structure for payload encryption
  const paddedMessage = createDataFrame(
    messagePayload,
    conversationId,
    senderProof,
    senderViewPublicKey,
    senderNextViewPublicKey,
    randomFill,
  );

  // === Layered ENCRYPTION ===

  // Optional post-quantum layer: encrypt the padded message with Kyber+ECDH combo
  let innerPayload = paddedMessage;
  if (kyberSecret) {
    const combinedSharedSecret = new Uint8Array(64);
    combinedSharedSecret.set(kyberSecret, 0);   // Kyber output in first half
    combinedSharedSecret.set(sharedSecret, 32);       // ECDH output in second half

    const layer2 = encryptLayer(nonce, paddedMessage, combinedSharedSecret, 2);
    innerPayload = layer2.ciphertext;
  }

  // Required outer encryption using ephemeral ECDH
  const encrypted = encryptLayer(nonce, innerPayload, sharedSecret, 1);
  const ciphertext = encrypted.ciphertext;

  // Guardrail: ciphertext must fit Solana account data limits
  if (ciphertext.length > MAX_PAYLOAD_SIZE + 2 * NACL_TAG_LENGTH) {
    throw new Error('Ciphertext exceeds maximum allowed length');
  }

  // === Instruction Buffer Assembly ===

  const totalLength = ENVELOPE_TOTAL_LENGTH + ciphertext.length + KEY_LENGTH;
  const instructionBuffer = Buffer.alloc(totalLength);

  // [0] Instruction discriminator (type: message post)
  instructionBuffer.writeUInt8(DISCRIMINATOR_MESSAGE, 0);

  // [1–8] Spam mitigation fee (required to deter message abuse)
  instructionBuffer.writeBigUInt64LE(spamMitigationFeeLamports, 1);

  // [9–...] Ciphertext (encrypted message)
  Buffer.from(ciphertext).copy(instructionBuffer, DISCRIMINATOR_LENGTH + FEE_LENGTH);

  // [...+32] Ephemeral public key (used for recipient-side ECDH)
  Buffer.from(ephemeralPublicKey).copy(
    instructionBuffer,
    DISCRIMINATOR_LENGTH + FEE_LENGTH + ciphertext.length
  );

  // [...+32] Shared secret hint (used to assist receiver in mapping to correct key)
  Buffer.from(sharedSecretHint).copy(
    instructionBuffer,
    DISCRIMINATOR_LENGTH + FEE_LENGTH + ciphertext.length + KEY_LENGTH
  );

  // [...+6] Filter tag (6 bytes): [1-byte bit length | 5-byte tag data], used by matcher for routing
  Buffer.from(filterTag).copy(
    instructionBuffer,
    DISCRIMINATOR_LENGTH + FEE_LENGTH + ciphertext.length + KEY_LENGTH + HINT_LENGTH
  );

  // === PDA: resolve fee enforcement authority account ===
  const [ pda_minimum_fee, _ ] = await PublicKey.findProgramAddress(
    [Buffer.from(MINIMUM_FEE_PDA_SEED)],
    PROGRAM_ID
  );

  const pubkey = new PublicKey(FAUCET_ID);

  const byteArray = pubkey.toBytes();

  // === Accounts required by the program ===
  const instructionKeys = [
    { pubkey: payer.publicKey, isSigner: true, isWritable: true },
    { pubkey: FAUCET_ID, isSigner: false, isWritable: true },
    { pubkey: FOUNDATION_ID, isSigner: false, isWritable: true },
    { pubkey: pda_minimum_fee, isSigner: false, isWritable: true },
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  // Return a well-formed Solana instruction
  return new TransactionInstruction({
    keys: instructionKeys,
    programId: PROGRAM_ID,
    data: instructionBuffer,
  });
}